import React from 'react';
import PropTypes from 'prop-types';
import Select, { components } from 'react-select';
import Tooltip from '@material-ui/core/Tooltip';
import { toast } from 'react-toastify';
import { addToCheckout, getCheckoutAddErrorMessage } from '../../utils/addToCheckout';
import { fetchCheckoutAddableSearch, getCheckoutSearchErrorMessage } from '../../utils/checkoutAddableSearch';
import './CheckoutAddItemSelect.scss';

const selectStyles = {
	control: (base, state) => ({
		...base,
		minHeight: 33,
		height: 33,
		fontSize: 14,
		borderColor: state.isFocused ? '#3f51b5' : 'rgba(0, 0, 0, 0.23)',
		borderRadius: 4,
		boxShadow: 'none',
		'&:hover': {
			borderColor: state.isFocused ? '#3f51b5' : 'rgba(0, 0, 0, 0.87)',
		},
	}),
	valueContainer: (base) => ({
		...base,
		height: 33,
		padding: '0 8px',
	}),
	input: (base) => ({
		...base,
		margin: 0,
		padding: 0,
	}),
	indicatorsContainer: (base) => ({
		...base,
		height: 33,
	}),
	dropdownIndicator: (base) => ({
		...base,
		padding: 4,
	}),
	clearIndicator: (base) => ({
		...base,
		padding: 4,
	}),
	menu: (base) => ({
		...base,
		zIndex: 20,
	}),
	groupHeading: (base) => ({
		...base,
		margin: 0,
		padding: 0,
	}),
	option: (base, state) => ({
		...base,
		fontSize: 14,
		padding: '6px 12px',
		backgroundColor: state.isSelected ? '#3f51b5' : state.isFocused ? '#f5f5f5' : '#fff',
		color: state.isSelected ? '#fff' : '#212529',
	}),
};

function formatGroupLabel(group) {
	return <div className="checkout-add-item-select__group-heading">{group.label}</div>;
}

function formatQuantityLabel(quantity, isSelected) {
	if (quantity == null || quantity === '') {
		return null;
	}

	return (
		<span
			className="checkout-add-item-select__option-qty"
			style={isSelected ? { color: 'rgba(255, 255, 255, 0.9)' } : undefined}
		>
			Qty {quantity}
		</span>
	);
}

function formatOptionLabel(option, meta) {
	const isSelected = meta && meta.isSelected;
	return (
		<div>
			<div className="checkout-add-item-select__option-label">
				<span className="checkout-add-item-select__option-label-text">{option.label}</span>
				{formatQuantityLabel(option.quantity, isSelected)}
			</div>
			{option.meta ? (
				<div
					className="checkout-add-item-select__option-meta"
					style={isSelected ? { color: 'rgba(255, 255, 255, 0.75)' } : undefined}
				>
					{option.meta}
				</div>
			) : null}
		</div>
	);
}

function CheckoutAddItemOption(props) {
	const description = props.data && props.data.description
		? String(props.data.description).trim()
		: '';
	const option = (
		<components.Option {...props}>
			{formatOptionLabel(props.data, { isSelected: props.isSelected })}
		</components.Option>
	);

	if (!description) {
		return option;
	}

	return (
		<Tooltip
			title={description}
			placement="right"
			arrow
			enterDelay={300}
			enterTouchDelay={500}
			leaveTouchDelay={3000}
		>
			<div className="checkout-add-item-select__option-wrap">
				{option}
			</div>
		</Tooltip>
	);
}

const selectComponents = {
	Option: CheckoutAddItemOption,
};

export default class CheckoutAddItemSelect extends React.Component {
	static propTypes = {
		disabled: PropTypes.bool,
		rootState: PropTypes.object,
		existingServiceIds: PropTypes.array,
		onItemAdded: PropTypes.func,
	};

	constructor(props) {
		super(props);
		this.state = {
			inputValue: '',
			selectedOption: null,
			groups: [],
			loading: false,
			loadError: null,
		};
		this.searchTimer = null;
	}

	componentWillUnmount() {
		if (this.searchTimer) {
			clearTimeout(this.searchTimer);
		}
	}

	loadGroups = async (searchTerm, showErrorToast = false) => {
		this.setState({ loading: true, loadError: null });
		try {
			const groups = await fetchCheckoutAddableSearch(searchTerm);
			this.setState({ groups, loading: false, loadError: null });
		} catch (error) {
			this.setState({ groups: [], loading: false, loadError: getCheckoutSearchErrorMessage(error) });
			if (showErrorToast) {
				toast.error(getCheckoutSearchErrorMessage(error), { position: toast.POSITION.TOP_RIGHT });
			}
		}
	};

	scheduleSearch = (searchTerm) => {
		if (this.searchTimer) {
			clearTimeout(this.searchTimer);
		}
		this.searchTimer = setTimeout(() => {
			void this.loadGroups(searchTerm, !!searchTerm);
		}, 300);
	};

	handleInputChange = (inputValue, { action }) => {
		if (action === 'input-change') {
			this.setState({ inputValue });
			this.scheduleSearch(inputValue);
		}
	};

	handleMenuOpen = () => {
		void this.loadGroups(this.state.inputValue, true);
	};

	handleChange = async (selectedOption) => {
		this.setState({ selectedOption: null, inputValue: '' });

		if (!selectedOption) {
			return;
		}

		try {
			const added = await addToCheckout(selectedOption, {
				rootState: this.props.rootState,
				existingServiceIds: this.props.existingServiceIds || [],
			});
			if (!added) {
				void this.loadGroups('');
				return;
			}
		} catch (error) {
			toast.error(getCheckoutAddErrorMessage(error), { position: toast.POSITION.TOP_RIGHT });
			void this.loadGroups(this.state.inputValue);
			return;
		}

		try {
			if (typeof this.props.onItemAdded === 'function') {
				await this.props.onItemAdded(selectedOption.type);
			}
		} catch (error) {
			console.error('Checkout reload after add failed', error);
		}

		void this.loadGroups('');
	};

	render() {
		const { groups, inputValue, loading, loadError } = this.state;
		const hasSearch = inputValue.trim().length > 0;

		return (
			<div className="checkout-add-item-select form-group">
				<Select
					className="checkout-add-item-select__control"
					classNamePrefix="checkout-add-item-select"
					placeholder="Add to checkout"
					options={groups}
					value={this.state.selectedOption}
					inputValue={inputValue}
					onInputChange={this.handleInputChange}
					onMenuOpen={this.handleMenuOpen}
					onChange={this.handleChange}
					isSearchable={true}
					isClearable={true}
					isLoading={loading}
					isDisabled={this.props.disabled}
					blurInputOnSelect={true}
					filterOption={() => true}
					formatGroupLabel={formatGroupLabel}
					formatOptionLabel={formatOptionLabel}
					components={selectComponents}
					styles={selectStyles}
					noOptionsMessage={() => {
						if (loadError) {
							return loadError;
						}
						return hasSearch ? 'No matches' : 'Type to search';
					}}
				/>
			</div>
		);
	}
}
